/**
 * Feature toggles configuration
 * Integrated with ConfigCat for remote configuration
 */
import * as configcat from "@configcat/sdk/browser";

export interface FeatureToggles {
  releaseNotes: boolean;
  enablePuppetHill: boolean;
  // Add more feature flags here as needed
}

/**
 * Feature toggle defaults and cache
 */
const toggleCache: FeatureToggles = {
  releaseNotes: false,
  enablePuppetHill: false,
};

// ConfigCat SDK Key
// Note: In Vite, env vars usually need VITE_ prefix to be exposed to the client.
// We check both for backward compatibility.
// const CONFIGCAT_SDK_KEY = import.meta.env.VITE_CONFIGCAT_SDK_KEY || import.meta.env.CONFIGCAT_SDK_KEY;
const CONFIGCAT_SDK_KEY = "configcat-sdk-1/06HeCCEoPUGGhJnSeKSTCA/VXn434pcYkSjU4QowHMwWA";

let configCatClient: configcat.IConfigCatClient | null = null;

if (CONFIGCAT_SDK_KEY) {
  const logger = configcat.createConsoleLogger(configcat.LogLevel.Info);
  configCatClient = configcat.getClient(
    CONFIGCAT_SDK_KEY,
    configcat.PollingMode.AutoPoll,
    { logger }
  );
}

/**
 * Initialize feature flags (call this at app start)
 */
export async function initFeatureFlags(): Promise<void> {
  if (!configCatClient) return;
  
  try {
    const keys = Object.keys(toggleCache) as Array<keyof FeatureToggles>;
    const results = await Promise.all(
      keys.map(key => configCatClient!.getValueAsync(key, toggleCache[key]))
    );
    
    keys.forEach((key, i) => {
      toggleCache[key] = !!results[i];
    });
  } catch (e) {
    console.warn("Failed to initialize feature flags from ConfigCat", e);
  }
}

/**
 * Check if a feature is enabled (Synchronous check against cache)
 * @param feature - Name of the feature to check
 * @returns Whether the feature is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureToggles): boolean {
  return toggleCache[feature] ?? false;
}

/**
 * Get all feature toggle states (Synchronous)
 * @returns Object containing all feature toggle states
 */
export function getAllFeatureToggles(): FeatureToggles {
  return { ...toggleCache };
}
